// La boîte à outils pour écrire des événements en série : chaque choix est décrit par ses effets,
// et ses textes suivent une convention de clés, vérifiée par tests/events-content.test.ts.
//
// Pour un événement « x » et un choix « c », les textes sont :
//   event.x.title        le titre
//   event.x.text         la situation
//   event.x.c            le bouton du choix
//   event.x.c.outcome    ce qui se passe ensuite
//   event.x.loan         le libellé du crédit, si un choix en fait un
// Les textes peuvent citer les paramètres de l'événement ({cost}, {gain}…), qui sont toujours
// des montants en euros. Après un crédit, le résultat peut aussi citer {payment} et {interest}.
import type { TextKey } from '../fr';
import type { EventChoice, EventDef, EventKind, EventParams } from '../events';
import { round2 } from '../../engine/finance';
import { fmtEur } from '../../engine/format';
import { randomInt } from '../../engine/rng';
import type {
  ExpenseId,
  GameState,
  MutuelleLevel,
  PageId,
  SkillId,
  StageId,
  SubscriptionId,
} from '../../engine/state';
import { DAYS_PER_YEAR, ageOnDay, dateAtDay } from '../../engine/time';
import {
  addLoan,
  addWellbeing,
  loseEnergy,
  payFromCash,
  payFromLivret,
  scheduleFollowUp,
  unlockPage,
} from '../../game/actions';
import { canOpenAv, depositToAv, openAv } from '../../game/av';
import { addXp } from '../../game/career';
import { livretOpen, monthlyIncome, recordIncome } from '../../game/economy';
import { setMutuelle } from '../../game/health';
import { depositToSavings, regulatedSavings } from '../../game/livret';
import { enjoyPurchase } from '../../game/treats';
import type { PleasureCategory } from '../treats';
import { changeSubscription, subscribe, unsubscribe } from '../../game/subscriptions';
import { renewUpgrade } from '../../game/upgrades';
import { cutMortgageRate, delegateMortgageInsurance } from '../../game/property';

/** Les lignes de dépenses qu'un choix change directement ; les abonnements ont leurs propres effets. */
type LineId = Exclude<ExpenseId, 'abonnements'>;

/** Ce que fait un choix. Tout est facultatif. */
export interface Effects {
  /** Compte courant : un gain s'il est positif, une dépense s'il est négatif (le découvert peut jouer). */
  cash?: number;
  /**
   * Livret : négatif, on paie avec (le choix est grisé si le livret ne suffit pas) ;
   * positif, on y range de l'argent (dans la tirelire tant que le livret n'est pas ouvert).
   * Payer avec le livret ne rapporte pas de bien-être en soi : il évite le découvert ou le crédit.
   */
  livret?: number;
  /** Bien-être gagné ou perdu, en points sur 100. */
  wellbeing?: number;
  /** Bien-être qu'on s'achète : il s'use si on s'offre souvent des plaisirs de la même catégorie. */
  pleasure?: number;
  /** La catégorie de ce plaisir, si elle diffère de celle de la carte. */
  pleasureCategory?: PleasureCategory;
  /** Énergie perdue : une valeur négative. */
  energy?: number;
  /** Un crédit à la consommation ; son libellé est le texte event.<id>.loan. */
  loan?: { amount: number; rate: number; months: number };
  /** Une dépense du mois prend ce montant, en euros par mois. */
  setExpense?: { id: LineId; amount: number };
  /** Une dépense du mois augmente de ce montant (ou baisse, s'il est négatif). */
  addExpense?: { id: LineId; amount: number };
  /** Un abonnement de plus, à ce prix par mois : il rejoint la ligne « Abonnements » du budget. */
  subscribe?: { id: SubscriptionId; amount: number };
  /** Un abonnement résilié : il quitte la ligne « Abonnements ». */
  unsubscribe?: SubscriptionId;
  /** Le prix d'un abonnement change de ce montant (une hausse, ou l'offre avec publicités). */
  changeSubscription?: { id: SubscriptionId; amount: number; withAds?: boolean };
  /** De l'expérience dans une compétence. */
  xp?: { skill: SkillId; amount: number };
  /** Une hausse de salaire (0.02 pour +2 %), seulement une fois le premier emploi commencé. */
  raise?: number;
  /** L'énergie revient deux fois moins vite pendant ce nombre de jours. */
  fatigueDays?: number;
  /** Pas de travail en extra pendant ce nombre de jours. */
  restDays?: number;
  /** Points de savoir. */
  knowledge?: number;
  /** Une page du carnet de Suzanne s'ouvre. */
  page?: PageId;
  /** Un repère pour une suite : un autre événement peut exiger state.flags[flag]. */
  flag?: string;
  /**
   * Une suite dans quelques jours : l'événement indiqué (déclaré avec manual: true) arrivera
   * au bout de ces jours de jeu, et ses textes pourront citer les montants de celui-ci.
   */
  followUp?: { event: string; days: number };
  /**
   * Change la formule de la mutuelle (« aucune » seulement pendant les études) ; son prix du mois suit,
   * sauf prix donné (0 pour la complémentaire santé solidaire, par exemple).
   */
  mutuelle?: { level: MutuelleLevel; monthly?: number };
  /**
   * Un appareil de la boutique remis à neuf (par exemple 'telephone' quand on en rachète un) ;
   * avec yearsLeft, une réparation qui lui rend seulement ces années de vie (une batterie neuve).
   */
  renew?: { upgrade: string; yearsLeft?: number };
  /** Le budget d'envies du mois change de ce montant (sans descendre sous zéro). */
  leisure?: number;
  /**
   * Un avantage durable, tant qu'il n'est pas retiré par endPerk : des points de bien-être visés
   * chaque mois, et une part d'abonnement qui compte encore comme des envies.
   */
  perk?: { id: string; wellbeing: number; leisure?: number };
  endPerk?: string;
  /** La garantie de la voiture : 'end' l'arrête (une révision sautée), un nombre d'années la prolonge. */
  carWarranty?: 'end' | number;
  /**
   * Un versement sur l'assurance-vie de la banque, pris sur les livrets (le choix est grisé s'ils ne
   * suffisent pas), avec ces frais d'entrée (0.03 pour 3 %) ; le contrat s'ouvre s'il ne l'est pas encore.
   */
  bankLifeInsurance?: { amount: number; entryFee: number };
  /** Le crédit immobilier passe à une assurance emprunteur déléguée, moins chère. */
  mortgageInsurance?: 'deleguee';
  /** Le taux du crédit immobilier baisse de tant (0.006 pour 0,6 point) : la mensualité suit. */
  mortgageRateCut?: number;
}

/** Des effets fixes, ou calculés d'après la situation : la fonction ne doit rien modifier ni tirer au hasard. */
type EffectsOf = Effects | ((state: GameState, params: EventParams) => Effects);

export interface SimpleChoice {
  id: string;
  effects?: EffectsOf;
  /** Le choix n'apparaît que dans certaines situations. */
  visible?: (state: GameState) => boolean;
  /** Le choix est grisé si cette règle n'est pas remplie (en plus de celle du livret). */
  available?: (state: GameState, params: EventParams) => boolean;
  /** Pourquoi il est grisé ; par défaut, « pas assez d'épargne ». */
  unavailable?: TextKey;
}

export interface SimpleEventDef {
  id: string;
  kind: EventKind;
  stages: StageId[];
  /** La catégorie des plaisirs que ses choix font acheter (repas, loisirs, voyages, achats, soins). */
  pleasureCategory?: PleasureCategory;
  /** Chances d'être tiré au sort, 1 par défaut. */
  weight?: number;
  /** Un coup dur : au moins deux mois entre deux, jamais trois de suite. */
  bad?: boolean;
  /** Bien-être perdu sous le choc, dès l'arrivée d'un coup dur. */
  shock?: number;
  /** Nombre de fois au plus dans une vie. */
  maxCount?: number;
  /** Délai avant de revenir, en jours de jeu (365 par défaut). */
  cooldownDays?: number;
  /** Les mois (1 à 12) où l'événement peut arriver : il y pèse vingt fois plus lourd au tirage. */
  season?: number[];
  /** Jamais tiré au sort : seulement une suite programmée par followUp. */
  manual?: boolean;
  condition?: (state: GameState) => boolean;
  /** Les montants cités par les textes ({cost}…), tirés une fois à l'arrivée de l'événement. */
  params?: (state: GameState) => EventParams;
  /** Page du carnet débloquée une fois l'événement réglé. */
  page?: PageId;
  choices: SimpleChoice[];
}

function resolve(effects: EffectsOf | undefined, state: GameState, params: EventParams): Effects {
  if (!effects) return {};
  return typeof effects === 'function' ? effects(state, params) : effects;
}

function euros(params: EventParams): Record<string, string> {
  return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, fmtEur(value)]));
}

function changeExpense(state: GameState, id: ExpenseId, amount: number): void {
  const value = Math.max(0, round2(amount));
  const expense = state.expenses[id];
  if (expense) expense.amount = value;
  else state.expenses[id] = { amount: value, examined: 0, optimized: false };
}

/** Applique les effets d'un choix ; renvoie les montants d'un éventuel crédit, pour le texte du résultat. */
function applyEffects(state: GameState, id: string, effects: Effects, params: EventParams): Record<string, string> {
  const extra: Record<string, string> = {};
  // L'argent reçu de l'extérieur compte dans les revenus de l'année ; un simple virement
  // du compte courant vers le livret (l'un négatif, l'autre positif) ne compte pas,
  // et l'argent emprunté non plus.
  recordIncome(state, (effects.cash ?? 0) + (effects.livret ?? 0) - (effects.loan?.amount ?? 0));
  if (effects.cash) {
    if (effects.cash > 0) state.cash = round2(state.cash + effects.cash);
    else payFromCash(state, -effects.cash);
  }
  if (effects.livret) {
    if (effects.livret < 0) {
      payFromLivret(state, -effects.livret);
    } else if (livretOpen(state)) {
      // Le Livret A d'abord, puis le LDDS ; au-delà des plafonds, l'argent reste sur le compte courant.
      const deposited = depositToSavings(state, effects.livret);
      state.cash = round2(state.cash + effects.livret - deposited);
      state.year.saved += deposited;
    } else {
      state.piggy = round2(state.piggy + effects.livret);
    }
  }
  if (effects.wellbeing) addWellbeing(state, effects.wellbeing);
  if (effects.pleasure) enjoyPurchase(state, effects.pleasure, effects.pleasureCategory ?? 'achats');
  if (effects.energy) loseEnergy(state, Math.abs(effects.energy));
  if (effects.loan) {
    const { amount, rate, months } = effects.loan;
    const loan = addLoan(state, `event.${id}.loan` as TextKey, amount, rate, months);
    extra.payment = fmtEur(loan.monthlyPayment);
    extra.interest = fmtEur(loan.totalInterest);
  }
  if (effects.setExpense) changeExpense(state, effects.setExpense.id, effects.setExpense.amount);
  if (effects.addExpense) {
    const { id: expense, amount } = effects.addExpense;
    changeExpense(state, expense, (state.expenses[expense]?.amount ?? 0) + amount);
  }
  if (effects.unsubscribe) unsubscribe(state, effects.unsubscribe);
  if (effects.subscribe) subscribe(state, effects.subscribe.id, effects.subscribe.amount);
  if (effects.changeSubscription) {
    const { id: sub, amount, withAds } = effects.changeSubscription;
    changeSubscription(state, sub, amount, withAds);
  }
  if (effects.xp) addXp(state, effects.xp.skill, effects.xp.amount);
  if (effects.raise && state.firstJobDay !== null) state.salary = round2(state.salary * (1 + effects.raise));
  if (effects.fatigueDays) state.fatigueUntilDay = Math.max(state.fatigueUntilDay, state.day + effects.fatigueDays);
  if (effects.restDays) state.restUntilDay = Math.max(state.restUntilDay, state.day + effects.restDays);
  if (effects.knowledge) state.knowledgePoints += effects.knowledge;
  if (effects.page) unlockPage(state, effects.page);
  if (effects.flag) state.flags = { ...state.flags, [effects.flag]: true };
  if (effects.bankLifeInsurance) {
    if (canOpenAv(state)) openAv(state, 'bank');
    depositToAv(state, effects.bankLifeInsurance.amount, 'livret', effects.bankLifeInsurance.entryFee);
  }
  if (effects.followUp) scheduleFollowUp(state, effects.followUp.event, effects.followUp.days, params);
  if (effects.mutuelle) setMutuelle(state, effects.mutuelle.level, effects.mutuelle.monthly);
  if (effects.renew) renewUpgrade(state, effects.renew.upgrade, effects.renew.yearsLeft);
  if (effects.leisure) state.budget.leisure = Math.max(0, round2(state.budget.leisure + effects.leisure));
  if (effects.perk) {
    const { id: perk, wellbeing, leisure = 0 } = effects.perk;
    state.perks = { ...state.perks, [perk]: { wellbeing, leisure } };
  }
  if (effects.endPerk) {
    const { [effects.endPerk]: _ended, ...rest } = state.perks;
    state.perks = rest;
  }
  if (effects.mortgageInsurance) delegateMortgageInsurance(state);
  if (effects.mortgageRateCut) cutMortgageRate(state, effects.mortgageRateCut);
  if (effects.carWarranty === 'end') state.carWarrantyUntil = Math.min(state.carWarrantyUntil, state.day);
  else if (effects.carWarranty) {
    state.carWarrantyUntil = Math.max(state.carWarrantyUntil, state.day) + effects.carWarranty * DAYS_PER_YEAR;
  }
  return extra;
}

/** Ce que fait un choix, hors moyen de paiement : deux choix qui ne diffèrent que par lui sont jumeaux. */
function besidesPayment(effects: Effects): string {
  const rest: Record<string, unknown> = { ...effects };
  delete rest.cash;
  delete rest.livret;
  return Object.keys(rest)
    .sort()
    .map((name) => `${name}:${JSON.stringify(rest[name])}`)
    .join(',');
}

/**
 * Le même paiement proposé deux fois, avec le livret et avec la carte, sans autre différence : renvoie
 * le moyen de paiement de ce choix et le montant, ou null si le choix n'a pas de jumeau visible.
 */
function twinPayment(
  choices: SimpleChoice[],
  choice: SimpleChoice,
  state: GameState,
  params: EventParams,
): { pays: 'livret' | 'cash'; amount: number } | null {
  const own = resolve(choice.effects, state, params);
  const pays = (own.livret ?? 0) < 0 ? 'livret' : (own.cash ?? 0) < 0 ? 'cash' : null;
  if (!pays) return null;
  const other = pays === 'livret' ? 'cash' : 'livret';
  if (own[other] !== undefined) return null;
  const amount = -(own[pays] ?? 0);
  const hasTwin = choices.some((item) => {
    if (item === choice || (item.visible && !item.visible(state))) return false;
    const effects = resolve(item.effects, state, params);
    return effects[other] === -amount && effects[pays] === undefined && besidesPayment(effects) === besidesPayment(own);
  });
  return hasTwin ? { pays, amount } : null;
}

/** Toutes les descriptions simples, pour que les tests vérifient leur contenu (la catégorie des plaisirs…). */
export const SIMPLE_EVENT_DEFS: SimpleEventDef[] = [];

/** Transforme une description simple en événement complet, avec ses clés de textes. */
export function simpleEvent(def: SimpleEventDef): EventDef {
  SIMPLE_EVENT_DEFS.push(def);
  const key = (suffix: string) => `event.${def.id}.${suffix}` as TextKey;
  const choices: EventChoice[] = def.choices.map((choice) => ({
    id: choice.id,
    label: key(choice.id),
    visible: (state, params) => {
      if (choice.visible && !choice.visible(state)) return false;
      // Payer au livret ce qu'on pourrait payer à la carte : le choix n'apparaît que si le compte courant ne suffit pas.
      const twin = twinPayment(def.choices, choice, state, params);
      return !(twin?.pays === 'livret' && state.cash >= twin.amount);
    },
    note: (state, params) => {
      // Payer à la carte plus qu'il n'y a sur le compte courant : le découvert s'annonce sous le bouton.
      const twin = twinPayment(def.choices, choice, state, params);
      if (twin?.pays !== 'cash' || state.cash >= twin.amount) return null;
      return { key: 'event.overdraftNote', params: { balance: fmtEur(round2(state.cash - twin.amount)) } };
    },
    available: (state, params) => {
      const { livret, loan, bankLifeInsurance } = resolve(choice.effects, state, params);
      if (loan && state.ficp) return false;
      if (livret !== undefined && livret < 0 && regulatedSavings(state) < -livret) return false;
      if (bankLifeInsurance && regulatedSavings(state) < bankLifeInsurance.amount) return false;
      return choice.available ? choice.available(state, params) : true;
    },
    unavailable: (state, params) => {
      const { loan } = resolve(choice.effects, state, params);
      if (loan && state.ficp) return 'event.ficpBloque';
      return choice.unavailable ?? 'event.notEnoughSavings';
    },
    apply: (state, params) => {
      const effects = resolve(choice.effects, state, params);
      const category = effects.pleasureCategory ?? def.pleasureCategory;
      const extra = applyEffects(state, def.id, { ...effects, pleasureCategory: category }, params);
      return { key: key(`${choice.id}.outcome`), params: { ...euros(params), ...extra } };
    },
  }));
  return {
    id: def.id,
    kind: def.kind,
    stages: def.stages,
    weight: def.weight ?? 1,
    bad: def.bad,
    shock: def.shock,
    maxCount: def.maxCount,
    cooldownDays: def.cooldownDays,
    season: def.season,
    manual: def.manual,
    condition: def.condition,
    params: def.params,
    page: def.page,
    title: key('title'),
    text: key('text'),
    choices,
  };
}

// Petites aides pour les conditions et les montants.

/** L'âge du personnage. */
export function age(state: GameState): number {
  return ageOnDay(state.birthDate, state.startDate, state.day);
}

/** Le mois du calendrier, de 1 (janvier) à 12 (décembre). */
export function month(state: GameState): number {
  return dateAtDay(state.startDate, state.day).getMonth() + 1;
}

/** Le premier emploi a commencé. */
export function working(state: GameState): boolean {
  return state.firstJobDay !== null;
}

/** Revenus du mois : le salaire, ou pendant les études la bourse, le job et l'aide des parents. */
export function income(state: GameState): number {
  return monthlyIncome(state);
}

/** Un nombre entier au hasard, sans lien avec les prix : un pile ou face, une proportion… (pour params). */
export function roll(state: GameState, min: number, max: number): number {
  return randomInt(state, min, max);
}

/**
 * Un montant au hasard entre deux bornes, en euros de départ, mis au niveau des prix du moment
 * (+2 % par bilan annuel) ; arrondi à 5 € au-delà de 100 €. À utiliser dans params.
 */
export function between(state: GameState, min: number, max: number): number {
  const value = randomInt(state, min, max) * state.priceLevel;
  return max >= 100 ? Math.round(value / 5) * 5 : Math.round(value);
}
